import { fetchCompanyProfile } from "../api/domainMonitor";
import type { CompanyProfile } from "../api/domainMonitor";
import { api } from "../api/client";

export type ProfileStatus =
  | "idle"
  | "starting"
  | "resolving"
  | "ready"
  | "not_found"
  | "low_confidence"
  | "error"
  | "timeout";

export interface ProfileState {
  domain: string;
  status: ProfileStatus;
  step: string;
  elapsedSeconds: number;
  profile: CompanyProfile | null;
  error: string | null;
  startedAt: number;
  canRetry?: boolean;
  attempt?: number;
}

type Listener = (state: ProfileState) => void;

class ProfileStore {
  private states: Map<string, ProfileState> = new Map();
  private listeners: Map<string, Set<Listener>> = new Map();
  private timers: Map<string, any> = new Map();
  private elapsedTimers: Map<string, any> = new Map();
  private lastPostTimes: Map<string, number> = new Map();

  constructor() {
    if (typeof window !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        if (!document.hidden) {
          // Resume polling for all active resolving domains
          for (const [domain, state] of this.states.entries()) {
            if (state.status === "starting" || state.status === "resolving") {
              this.schedulePoll(domain);
            }
          }
        }
      });
    }
  }

  public normalizeDomain(rawDomain: string): string {
    if (!rawDomain) return "";
    return rawDomain.trim().toLowerCase().replace(/^(https?:\/\/)?(www\.)?/, "").split("/")[0].split(":")[0];
  }

  public getState(domain: string): ProfileState {
    const canonical = this.normalizeDomain(domain);
    if (!canonical) {
      return {
        domain: "",
        status: "idle",
        step: "",
        elapsedSeconds: 0,
        profile: null,
        error: null,
        startedAt: 0
      };
    }

    if (!this.states.has(canonical)) {
      this.states.set(canonical, {
        domain: canonical,
        status: "idle",
        step: "",
        elapsedSeconds: 0,
        profile: null,
        error: null,
        startedAt: 0,
        canRetry: false,
        attempt: 0
      });
    }
    return this.states.get(canonical)!;
  }

  public subscribe(domain: string, listener: Listener): () => void {
    const canonical = this.normalizeDomain(domain);
    if (!canonical) return () => {};

    if (!this.listeners.has(canonical)) {
      this.listeners.set(canonical, new Set());
    }
    this.listeners.get(canonical)!.add(listener);

    // Immediately call listener with current state
    listener(this.getState(canonical));

    return () => {
      const set = this.listeners.get(canonical);
      if (set) {
        set.delete(listener);
      }
    };
  }

  private notify(canonical: string) {
    const state = this.getState(canonical);
    const set = this.listeners.get(canonical);
    if (set) {
      set.forEach(fn => fn(state));
    }
  }

  private setState(canonical: string, patch: Partial<ProfileState>) {
    const current = this.getState(canonical);
    const updated = { ...current, ...patch };
    this.states.set(canonical, updated);
    this.notify(canonical);
  }

  public resetAll() {
    this.states.clear();
    for (const [canonical, timer] of this.elapsedTimers.entries()) {
      if (timer) clearInterval(timer);
    }
    this.elapsedTimers.clear();
    for (const [canonical, timer] of this.timers.entries()) {
      if (timer) clearTimeout(timer);
    }
    this.timers.clear();
    this.lastPostTimes.clear();
  }

  public resetProfile(domain: string) {
    const canonical = this.normalizeDomain(domain);
    if (!canonical) return;
    this.setState(canonical, {
        status: "idle",
        step: "",
        elapsedSeconds: 0,
        profile: null,
        error: null,
        startedAt: 0,
        canRetry: false,
        attempt: 0
    });
  }

  public async start(domain: string, forceRefresh = false): Promise<void> {
    const canonical = this.normalizeDomain(domain);
    if (!canonical) return;

    const currentState = this.getState(canonical);

    // 5-second debounce check on POST resolve calls
    const lastPost = this.lastPostTimes.get(canonical) || 0;
    const now = Date.now();
    const isDebounced = (now - lastPost) < 5000;

    // Transition immediately to starting state within <100ms
    if (currentState.status === "idle" || !isDebounced || forceRefresh) {
      this.setState(canonical, {
        status: "starting",
        step: "Searching the web...",
        error: null,
        startedAt: currentState.startedAt || Date.now()
      });
      this.startElapsedTimer(canonical);
    }

    // Suppress duplicate network POSTs if debounced or already resolving
    if (isDebounced && currentState.status === "resolving" && !forceRefresh) {
      return;
    }

    this.lastPostTimes.set(canonical, now);

    try {
      const res = await api.post("/report-center/profile/resolve", { domain: canonical });
      if (res.status === 200 && res.data.profile) {
        this.stopElapsedTimer(canonical);
        const p: CompanyProfile = res.data.profile;
        const st: ProfileStatus = p.status === "not_found" ? "not_found" : p.confidence < 0.5 ? "low_confidence" : "ready";
        this.setState(canonical, {
          status: st,
          step: "Complete",
          profile: p,
          error: null
        });
        return;
      }

      // 202 Resolving
      this.setState(canonical, {
        status: "resolving",
        step: res.data.step || "Identifying company and sector...",
        startedAt: currentState.startedAt || Date.now()
      });
      this.schedulePoll(canonical, 1000);
    } catch (err: any) {
      // Fallback check status or GET profile
      try {
        const prof = await fetchCompanyProfile(canonical, forceRefresh);
        this.stopElapsedTimer(canonical);
        const st: ProfileStatus = prof.status === "not_found" ? "not_found" : prof.confidence < 0.5 ? "low_confidence" : "ready";
        this.setState(canonical, {
          status: st,
          step: "Complete",
          profile: prof,
          error: null
        });
      } catch (fallbackErr: any) {
        this.setState(canonical, {
          status: "resolving",
          step: "Identifying company...",
          startedAt: currentState.startedAt || Date.now()
        });
        this.schedulePoll(canonical, 1000);
      }
    }
  }

  public async reconnect(domain: string): Promise<void> {
    const canonical = this.normalizeDomain(domain);
    if (!canonical) return;

    const current = this.getState(canonical);
    if (current.status === "ready" || current.status === "not_found" || current.status === "low_confidence") {
      return;
    }

    this.setState(canonical, {
      status: "resolving",
      step: "Checking profile status...",
      startedAt: current.startedAt || Date.now()
    });
    this.startElapsedTimer(canonical);
    this.pollStatus(canonical);
  }

  public async retry(domain: string): Promise<void> {
    const canonical = this.normalizeDomain(domain);
    if (!canonical) return;

    this.setState(canonical, {
      status: "resolving",
      step: "Retrying analysis on stored evidence...",
      error: null,
      startedAt: Date.now()
    });
    this.startElapsedTimer(canonical);

    try {
      await api.post("/report-center/profile/retry", { domain: canonical });
      this.schedulePoll(canonical, 1000);
    } catch (err: any) {
      this.setState(canonical, {
        status: "error",
        step: "Failed",
        error: "Failed to trigger retry"
      });
      this.stopElapsedTimer(canonical);
    }
  }

  private schedulePoll(canonical: string, delayMs?: number) {
    if (this.timers.has(canonical)) {
      clearTimeout(this.timers.get(canonical));
    }

    const state = this.getState(canonical);
    if (state.status !== "starting" && state.status !== "resolving") {
      return;
    }



    // Calculate delay: 1s, 1.5s, 2s, then 3s
    let nextDelay = delayMs;
    if (!nextDelay) {
      const el = state.elapsedSeconds;
      if (el < 5) nextDelay = 1000;
      else if (el < 15) nextDelay = 1500;
      else if (el < 30) nextDelay = 2000;
      else nextDelay = 3000;
    }

    const timer = setTimeout(() => {
      if (document.hidden) {
        // Pause polling while tab is hidden
        return;
      }
      this.pollStatus(canonical);
    }, nextDelay);

    this.timers.set(canonical, timer);
  }

  private async pollStatus(canonical: string) {
    const state = this.getState(canonical);
    if (state.status !== "starting" && state.status !== "resolving") {
      return;
    }

    try {
      const res = await api.get(`/report-center/profile/status?domain=${encodeURIComponent(canonical)}`);
      const data = res.data;

      if (data.status === "ready" && data.profile) {
        this.stopElapsedTimer(canonical);
        const p: CompanyProfile = data.profile;
        const st: ProfileStatus = p.status === "not_found" ? "not_found" : p.confidence < 0.5 ? "low_confidence" : "ready";
        this.setState(canonical, {
          status: st,
          step: "Complete",
          profile: p,
          error: null,
          attempt: data.attempt || 0,
        });
        return;
      } else if (data.status === "not_found" && data.profile) {
        this.stopElapsedTimer(canonical);
        this.setState(canonical, {
          status: "not_found",
          step: "Sector confirmation needed",
          profile: data.profile,
          error: null,
          attempt: data.attempt || 0,
        });
        return;
      } else if (data.status === "failed" || data.status === "failed_transient" || data.status === "failed_blocked") {
        this.stopElapsedTimer(canonical);
        this.setState(canonical, {
          status: "error",
          step: "Failed",
          error: data.reason || data.error || "Failed to resolve company profile",
          canRetry: data.can_retry || false,
          attempt: data.attempt || 0,
        });
        return;
      }

      // Still resolving
      this.setState(canonical, {
        status: "resolving",
        step: data.step || "Identifying company and sector...",
        attempt: data.attempt || 0,
        elapsedSeconds: data.elapsed ? Math.floor(data.elapsed) : this.getState(canonical).elapsedSeconds
      });
      this.schedulePoll(canonical);
    } catch (err: any) {
      // Retry poll after 2s on network error
      this.schedulePoll(canonical, 2000);
    }
  }

  private startElapsedTimer(canonical: string) {
    if (this.elapsedTimers.has(canonical)) return;

    const timer = setInterval(() => {
      const current = this.getState(canonical);
      if (current.status === "starting" || current.status === "resolving") {
        const nextSec = current.elapsedSeconds + 1;
        this.setState(canonical, { elapsedSeconds: nextSec });

      } else {
        this.stopElapsedTimer(canonical);
      }
    }, 1000);

    this.elapsedTimers.set(canonical, timer);
  }

  private stopElapsedTimer(canonical: string) {
    if (this.elapsedTimers.has(canonical)) {
      clearInterval(this.elapsedTimers.get(canonical));
      this.elapsedTimers.delete(canonical);
    }
    if (this.timers.has(canonical)) {
      clearTimeout(this.timers.get(canonical));
      this.timers.delete(canonical);
    }
  }

  public updateProfileSector(domain: string, newProfile: CompanyProfile) {
    const canonical = this.normalizeDomain(domain);
    if (!canonical) return;
    this.setState(canonical, {
      status: "ready",
      profile: newProfile,
      error: null
    });
  }
}

export const profileStore = new ProfileStore();
